import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../config';
import { motion } from 'motion/react';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import LandingHeader from '../components/LandingHeader';
import Footer from '../components/Footer';
import SupportIdDisplay from '../components/SupportIdDisplay';

import {
  ChatText,
  CheckCircle,
  Envelope,
  Lifebuoy,
  PaperPlaneRight,
  Shield,
  WarningCircle
} from '@phosphor-icons/react';
const Contact = () => {
  const [activeTab, setActiveTab] = useState('enquiries'); // 'enquiries' or 'support'
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const springConfig = {
    type: 'spring',
    damping: 80,
    stiffness: 400,
    mass: 1,
  };

  // Check if user is logged in
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch(
          `${BACKEND_URL}/api/auth/me`,
          {
            credentials: 'include'
          }
        );
        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
        }
      } catch (error) {
        // User not logged in
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    
    checkAuth();
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // NOTE: Do NOT track conversion here - contact form is not a signup
    // Conversion only fires on ACTUAL account creation
    
    setIsSubmitting(true);

    try {
      const endpoint = activeTab === 'support' ? '/api/support/ticket' : '/api/contact';
      
      const response = await fetch(
        `${BACKEND_URL}${endpoint}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            ...formData,
            submission_type: activeTab,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      setSubmitted(true);
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        company: '',
        message: '',
      });
      
      // Hide success message after 5 seconds
      setTimeout(() => setSubmitted(false), 5000);
      
    } catch (error) {
      toast.error('Failed to send message. Please try again.', {
        duration: 4000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-[#161B26]">
      <SEO 
        title="Contact Man & Machine - Get Started with AI & Business Solutions"
        description="Ready to transform your business? Contact Man & Machine for expert AI consulting, business intelligence, and marketing solutions. Book your pilot project or request a consultation today."
        keywords="contact AI consultant, business intelligence consultation, AI pilot project, digital transformation consultation"
        canonicalUrl="https://manmachine.agency/contact"
      />
      <LandingHeader />
      
      
      {/* Hero Section */}
      <section className="py-32 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#6366F1] rounded-full filter blur-3xl opacity-20"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#EC4899] rounded-full filter blur-3xl opacity-20"></div>
        </div>
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springConfig}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="inline-flex p-4 rounded-xl bg-[rgba(99,102,241,0.1)] mb-6">
              <Envelope size={40} className="text-[#6366F1]" />
            </div>
            <h1 className="display-hero text-white mb-6">
              Let's Build Something <span className="text-[#6366F1]">Brilliant</span>
            </h1>
            <p className="body-large text-[#A0AEC0] max-w-3xl mx-auto">
              Ready to unlock smarter growth with AI? Tell us about your goals and we'll show you what's possible.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 bg-[#161B26]">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
          >
            <div className="glass rounded-3xl p-8 md:p-12 card-glow">
              {/* Tabs */}
              <div className="flex gap-4 mb-8 border-b border-[rgba(255,255,255,0.1)]">
                <button
                  type="button"
                  onClick={() => setActiveTab('enquiries')}
                  className={`flex items-center gap-2 px-6 py-3 body-standard font-medium transition-all relative ${
                    activeTab === 'enquiries'
                      ? 'text-[#6366F1]'
                      : 'text-[#718096] hover:text-white'
                  }`}
                >
                  <ChatText size={20} />
                  Enquiries
                  {activeTab === 'enquiries' && (
                    <motion.div
                      layoutId="activeTab"
                      className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#6366F1]"
                      transition={springConfig}
                    />
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('support')}
                  className={`flex items-center gap-2 px-6 py-3 body-standard font-medium transition-all relative ${
                    activeTab === 'support'
                      ? 'text-[#6366F1]'
                      : 'text-[#718096] hover:text-white'
                  }`}
                >
                  <Lifebuoy size={20} />
                  Support
                  {activeTab === 'support' && (
                    <motion.div
                      layoutId="activeTab"
                      className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#6366F1]"
                      transition={springConfig}
                    />
                  )}
                </button>
              </div>

              {/* Tab Description */}
              <div className="mb-6">
                {activeTab === 'enquiries' ? (
                  <p className="body-standard text-[#A0AEC0]">
                    Have a question about Man & Machine, our platform, or how we can help your business grow? Send us a message and our team will get back to you shortly.
                  </p>
                ) : (
                  <p className="body-standard text-[#A0AEC0]">
                    Need help with your account, billing, agents, or platform usage? Our support team is here to help.
                  </p>
                )}
              </div>

              {/* Support IDs - Show if logged in and on support tab */}
              {activeTab === 'support' && (
                <div className="mb-6">
                  {user ? (
                    <SupportIdDisplay 
                      userId={user.id} 
                      organizationId={user.organization_id}
                      variant="compact"
                    />
                  ) : (
                    <div className="bg-[#1A1B23] border border-[#2D2E3A] rounded-lg p-4">
                      <p className="text-sm text-[#A0AEC0]">
                        <a href="/login" className="text-[#6366F1] hover:text-[#EC4899] underline">Log in</a> to view your support identifiers for faster assistance.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Support Tab - Login Required ChatText */}
              {activeTab === 'support' && !loading && !user && (
                <div className="mb-6 p-6 rounded-2xl bg-[rgba(255,107,107,0.1)] border border-[rgba(255,107,107,0.3)]">
                  <div className="flex items-start gap-4">
                    <WarningCircle size={24} className="text-[#FF6B6B] flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-white font-semibold mb-2">Login Required</h3>
                      <p className="text-[#A0AEC0] text-sm mb-4">
                        Please log in to access support, or use the Enquiries tab for general questions.
                      </p>
                      <a
                        href="/login"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#6366F1] hover:bg-[#5558E6] text-white font-semibold text-sm transition-colors"
                      >
                        Log In
                      </a>
                    </div>
                  </div>
                </div>
              )}

              {/* Success ChatText */}
              {submitted && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="mb-6 p-6 rounded-2xl bg-[rgba(20,241,149,0.1)] border border-[rgba(20,241,149,0.3)]"
                >
                  <div className="flex items-start gap-4">
                    <CheckCircle size={24} className="text-[#14F195] flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-white font-semibold mb-2">
                        {activeTab === 'support' ? 'Support Request Submitted' : 'Message Received'}
                      </h3>
                      <p className="text-[#A0AEC0] text-sm">
                        {activeTab === 'support'
                          ? "Support request submitted. You'll receive updates via email and inside the platform."
                          : "Thanks — we've received your message. A member of our team will be in touch within 1 business day."}
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Form - Hide if support tab and not logged in */}
              {!(activeTab === 'support' && !user && !loading) && (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Name Field */}
                  <div>
                    <label htmlFor="name" className="block body-standard font-medium text-white mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-6 py-4 rounded-full glass text-white focus:outline-none focus:ring-2 focus:ring-[#6366F1] transition-colors body-standard placeholder-[#718096]"
                      placeholder="Your full name"
                    />
                  </div>

                  {/* Email Field */}
                  <div>
                    <label htmlFor="email" className="block body-standard font-medium text-white mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-6 py-4 rounded-full glass text-white focus:outline-none focus:ring-2 focus:ring-[#6366F1] transition-colors body-standard placeholder-[#718096]"
                      placeholder="your@email.com"
                    />
                  </div>

                  {/* Company Field */}
                  <div>
                    <label htmlFor="company" className="block body-standard font-medium text-white mb-2">
                      Company
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full px-6 py-4 rounded-full glass text-white focus:outline-none focus:ring-2 focus:ring-[#6366F1] transition-colors body-standard placeholder-[#718096]"
                      placeholder="Your company name"
                    />
                  </div>

                  {/* ChatText Field */}
                  <div>
                    <label htmlFor="message" className="block body-standard font-medium text-white mb-2">
                      ChatText *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows="6"
                      className="w-full px-6 py-4 rounded-3xl glass text-white focus:outline-none focus:ring-2 focus:ring-[#6366F1] transition-colors body-standard resize-none placeholder-[#718096]"
                      placeholder="Tell us about your project, goals, or questions..."
                    ></textarea>
                  </div>

                  {/* Privacy Notice */}
                  <div className="p-4 rounded-xl bg-[rgba(20,241,149,0.05)] border border-[rgba(20,241,149,0.2)] flex items-start gap-3">
                    <Shield size={18} className="text-[#14F195] flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-[#A0AEC0] leading-relaxed">
                      We respect your privacy. Your information is used only to respond to your enquiry or support request. Marketing emails are opt-in only and disabled by default.
                    </p>
                  </div>

                  {/* Submit Button */}
                  <div className="flex justify-center pt-4">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="inline-flex items-center justify-center gap-2 px-12 py-4 rounded-full bg-[#6366F1] hover:bg-[#5558E6] text-white font-semibold text-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isSubmitting ? (
                          'Sending...'
                        ) : (
                          <>
                            {activeTab === 'support' ? 'Create Support Ticket' : 'Send Enquiry'}
                            <PaperPlaneRight size={20} />
                          </>
                        )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;
